import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {selectGameType, selectLastCapturedCities} from '../reducers/sharedStateReducer/selectors';
import {GAME_TYPES} from '../../constants/game';
import {checkIfReplayRewinding} from '../../utils/replay/replayPlaybackSpeed';

// Полное состояние (переподключение, ресинк) захват заново не показывает — как
// и модалку захвата в modalsMiddleware
const IGNORED_ACTIONS = ['LOAD_STATE', 'RESET_STATE'];

// С этих действий запись матча начинается заново: перемотка к началу, новая
// запись или новая партия ботов на витрине меню
const RESTART_ACTIONS = ['LOAD_STATE', 'RESET_STATE', 'START_GAME'];

/**
 * Запись матча перемотали (или витрина меню начала новую партию), пока шел
 * облет: он показывает уже не то поле — обрываем. В живой партии полное
 * состояние (ресинк, переподключение) облет не обрывает: поле то же
 */
const checkIfCaptureAnimationOutdated = (state, scene) => {
  if (!scene?.cityCaptureAnimation?.isActive) {
    return false;
  }
  if (checkIfReplayRewinding()) {
    return true;
  }
  return scene.isDemoMode || selectGameType(state) === GAME_TYPES.REPLAY;
};

/**
 * Облет захваченных городов на поле (см. scenes/CityCaptureAnimation)
 * запускается здесь — сразу после того, как новый список захватов
 * (gameState.lastCapturedCities) прошел редьюсер.
 *
 * Стоит в цепочке ПОСЛЕ modalsMiddleware: та поднимает флаг модалки
 * захвата, когда управление вернется к ней из next, — а к этому моменту облет
 * уже поднял свой флаг, и модалка ни на кадр не покажется раньше облета. Облет
 * не запустился (нет сцены, запись матча на скорости выше x5) — модалка
 * показывается сразу, как раньше.
 *
 * Песочницы ботов и сервер считают захват на своих сторах без этого
 * middleware — там облета нет и быть не должно.
 */
export const createCityCaptureAnimationMiddleware = ({getGameScene}) => (
  (store) => (next) => (action) => {
    if (!action) {
      return next(action);
    }
    // Сцену спрашиваем только тогда, когда облет может устареть или начаться:
    // middleware стоит на каждом экшене
    if (checkIfReplayRewinding() || RESTART_ACTIONS.includes(action.type)) {
      const scene = getGameScene();
      if (checkIfCaptureAnimationOutdated(store.getState(), scene)) {
        try {
          scene.cityCaptureAnimation.abort({restoreCamera: true});
        } catch (error) {
          console.warn('[CityCapture] не удалось оборвать облет', error);
        }
      }
    }
    if (IGNORED_ACTIONS.includes(action.type)) {
      return next(action);
    }

    const previousCapturedCities = selectLastCapturedCities(store.getState());
    const result = next(action);
    const capturedCities = selectLastCapturedCities(store.getState());

    if (capturedCities?.length > 0 && capturedCities !== previousCapturedCities) {
      try {
        getGameScene()?.cityCaptureAnimation?.start(capturedCities);
      } catch (error) {
        // Анимация — украшение: ее поломка не должна ронять диспатч
        console.warn('[CityCapture] не удалось запустить облет захваченных городов', error);
      }
    }

    return result;
  }
);

const cityCaptureAnimationMiddleware = createCityCaptureAnimationMiddleware({
  getGameScene: () => REGISTRY_SINGLETON.getGameScene?.(),
});

export default cityCaptureAnimationMiddleware;
